import React, { useState, useEffect } from 'react'

import { Formik } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux'


import { getUserRolesAndPermissions, createUser, cleanUpCreateUser } from '../../../store/users/users.actions';
import { getAllCustomers } from '../../../store/customers/customers.actions';

// Components

import FormInput from '../../UI/FormInput/FormInput';
import FormSteps from '../../UI/FormSteps/FormSteps';
import SelectInput from '../../UI/FormInput/SelectInput';
import Loader from '../../UI/Loader/Loader';
import Button from '../../UI/Button/Button';
import SwitchInput from '../../UI/FormInput/SwitchInput';
import PermissionsList from '../../lists/PermissionsList/PermissionsList';

// Utilities

import { formatRolesOptionsData, formatPermissionsOptionsData } from '../../../utilities/helper-functions';

const steps = [
  'Datos generales',
  'Permisos'
]

export default function AddUserForm({setShowAddUserModal}) {
  const [formStep, setFormStep] = useState(0);
  const userCreate = useSelector((state) => state.users.createUser);
  const rolesAndPermissions = useSelector((state) => state.users.rolesAndPermissions);
  const customers = useSelector((state) => state.customers.customersAll);
  const user = useSelector((state) => state.auth.user);
  const userPermissions = user?.data?.permissionsData;

  const dispatch = useDispatch();

  // Create a user

  const createNewUser = async (values, actions) => {

    if (formStep === 1) {
      dispatch(createUser(values));
    } else {
      setFormStep(formStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }

  }

  useEffect(() => {
    if (userCreate.isCreated) {
      setShowAddUserModal(false);
      dispatch(cleanUpCreateUser());
    }
  }, [dispatch, setShowAddUserModal, userCreate])

  useEffect(() => {
    dispatch(getAllCustomers());
    dispatch(getUserRolesAndPermissions());
  }, [dispatch])

  return (
    <>
      {(customers?.isLoading || customers?.error || rolesAndPermissions?.isLoading || rolesAndPermissions?.error) &&
        <Loader
          message={'Cargando...'}
          error={customers.error || rolesAndPermissions?.error}
          action={() => {
            dispatch(getAllCustomers());
            dispatch(getUserRolesAndPermissions());
          }}
          dark={true}
      />}

      {(!customers?.isLoading && customers?.data && !rolesAndPermissions?.isLoading && rolesAndPermissions?.data) &&
      <>

        <FormSteps steps={steps} currentStep={formStep} />
  
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema[formStep]}
          onSubmit={(values, actions) => createNewUser(values, actions)}
        >

          {props => (
          <>

            {userCreate.error && <p className="form-error-message">{userCreate.error}</p>}

            {/* Form Inputs */}

            {formStep === 0 &&
            <>
              <SwitchInput
                formik={props}
                fieldName={'status'}
                label={'Activo'}
              />

              {customers && customers?.data &&
              <SelectInput
                formik={props}
                fieldName={'customer_id'}
                label={'Cliente'}
                placeholder={'Seleccione'}
                options={customers?.data ? customers?.data.map(customer => ({ value: customer.id, label: customer.name })) : []}
              />}

              <FormInput
                  formik={props}
                  fieldName={'name'}
                  label={'Nombre'}
              />

              <FormInput
                  formik={props}
                  fieldName={'lastname'}
                  label={'Apellido'}
              />

              <FormInput
                  formik={props}
                  fieldName={'email'}
                  label={'Email'}
              />

              <FormInput
                  formik={props}
                  fieldName={'password'}
                  type={'password'}
                  label={'Contraseña'}
              />

              <FormInput
                  formik={props}
                  fieldName={'repassword'}
                  type={'password'}
                  label={'Repetir Contraseña'}
              />

              <p style={{textAlign: 'right'}}><small>* Contraseña de minimo 8 caracteres</small></p>
            
              <div className='form-actions'>

                <Button
                  action={() => setShowAddUserModal(false)}
                  label={'Cancelar'}
                  secondaryStyle={true}
                />

                <Button
                  action={props.handleSubmit}
                  label={'Continuar'}
                />

              </div>
            </>}

            {formStep === 1 &&
            <>
              <SelectInput
                formik={props}
                fieldName={'role'}
                label={'Rol'}
                placeholder={'Seleccione'}
                options={formatRolesOptionsData(rolesAndPermissions?.data, userPermissions)}
                additionalFunctions={() => props.setFieldValue('permissions', [])}
              />

              {props.values.role &&
              <PermissionsList
                data={formatPermissionsOptionsData(rolesAndPermissions?.data, props.values.role)}
                formik={props}
                fieldName={'permissions'}
              />}

              <div className='form-actions'>

              <Button
                action={() => setFormStep(formStep - 1)}
                label={'Atrás'}
                disabled={userCreate?.isLoading}
                secondaryStyle={true}
              />

              <Button
                action={props.handleSubmit}
                label={'Añadir'}
                disabled={userCreate?.isLoading}
                isLoading={userCreate?.isLoading}
              />

              </div>
            </>}
            
          </>)}

        </Formik>

      </>}
    </>
  )
}

// Form initial values and validaiton schemas

const initialValues = { 
  customer_id: '',
  name: '', 
  lastname: '',
  email: '',
  password: '',
  repassword: '',
  status: 0,
  role: '',
  permissions: [],
};

const validationSchema = [
  yup.object().shape({
    customer_id: yup
      .string()
      .required('Este campo es requerido'),
    name: yup
      .string()
      .required('Este campo es requerido'),
    lastname: yup
      .string()
      .required('Este campo es requerido'),
    email: yup
      .string()
      .email('Ingrese un email válido.')
      .required('Este campo es requerido'),
    password: yup
      .string()
      .matches(/^\S+$/, { message: 'Contraseña no puede tener espacios' })
      .min(8, 'Contraseña de minimo 8 caracteres')
      .required('Este campo es requerido'),
    repassword: yup
      .string()
      .oneOf(
          [yup.ref('password')], 'Las contraseñas no coinciden',
      )
      .required('Este campo es requerido'),
    status: yup
      .number(),
  }),
  yup.object().shape({
    role: yup
      .string()
      .required('Este campo es requerido'),
    permissions: yup
      .array()
      .min(1, 'Seleccione por lo menos un permiso')
      .required('Este campo es requerido'),
  }),
];